/**
 * The proposal — the one document a customer reads.
 *
 * It is rendered in exactly three places, from this one component: the
 * customer portal, where it is the real thing; the builder's Proposal tab,
 * where it is a live preview; and `/quotes/:id/preview`, where it is the same
 * page opened by the office in its own tab. That is deliberate and it is the
 * only defensible arrangement (`docs/proposal.md` §2 rule 3) — a "preview"
 * that is a second rendering of the same data is a preview that will one day
 * disagree with what was sent, and the disagreement will be discovered by a
 * customer.
 *
 * **It takes a `ProposalView`, never a `QuoteTree`** (§2 rule 1). It used to
 * take the tree and simply not draw the costs, margins and crew notes in it —
 * which is not the same thing as not sending them, and anybody who opened the
 * network tab on their own quote read our margin on their own job. The
 * filtering is a projection on the server (`server/src/services/proposal.ts`),
 * written once, with a test that fails if a forbidden key appears anywhere in
 * the JSON. There is nothing left on this page to filter.
 *
 * **Layout.** The band, the intro card overlapping it by 76px, the section
 * chips, the body, and from `RAIL_FROM` (1280px) up a 344px sticky rail
 * beside it. Below that the rail's two lines become the fixed bottom bar, and
 * the footer clears whatever height the bar measures. **Three layouts and one
 * rule for choosing them** (`ProposalLayout`): the window decides on a real
 * screen, and the builder's preview frame says which one it is drawing,
 * because a 820px frame inside a 1600px window has the window's media queries.
 *
 * **Colour.** Nine CSS variables from `brandSkin()`, set once on the root
 * element; every component under here reads `bg-brand`, `text-brand-tx` and
 * friends and never a hex (§13). `brandPalette()` is the one thing that
 * decided what the company's saved colour may be used for.
 */
import { useMemo, useState } from 'react'
import {
  monthSplit, selectionTotals,
  type ProposalSelection, type ProposalView, type SignaturePayload,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { useNarrow, usePhone } from '../../lib/use-narrow.js'
import { RowtedMark } from '../../frame/logo.js'
import { brandSkin } from './brand.js'
import { Lockup, ProposalTopBar } from './top-bar.js'
import { ProposalCover } from './cover.js'
import { ProposalIntro } from './intro-card.js'
import { ProposalSectionNav, type ProposalSection } from './section-nav.js'
import { ProposalRail, depositOf } from './summary-rail.js'
import { ProposalStickyBar, PROPOSAL_BAR } from './sticky-bar.js'
import { ProposalTermsCard } from './terms-card.js'
import { monthLine } from './month-view.js'
import { monthOffered, useMonthMode } from './month-toggle.js'
import { usePrice, type PriceCall } from './use-price.js'
import {
  ChoiceSection, IncludedSection, OptionalSection, SECTION_IDS, SignSection,
  SignedSection, SignedTermsStamp, TotalSection,
  type BodyProps,
} from './body.js'
import { ResponseDialog, type ResponseApi, type ResponseKind } from './responses.js'
import { closedPage } from './states.js'

/**
 * The three ways the page lays itself out.
 *
 *   phone    one column edge to edge, the bottom bar, sheets from below
 *   tablet   one centred 740px column, the bottom bar, centred sheets
 *   desktop  the column and the 344px summary rail beside it
 */
export type ProposalLayout = 'phone' | 'tablet' | 'desktop'

/**
 * The narrowest window that gets the summary rail — **1280, the page's own
 * width** (Aaron's review, item 2.6).
 *
 * It was 1100, board 9's number, and an iPad in landscape is 1180: the rail
 * took 380px of it and left the column 680, so every visit card, price block
 * and payment row was the desktop layout squeezed into a phone's worth of
 * room — "everything is truncated and wrapped". Aaron: "eliminate the right
 * sidebar component and let every other line have some space". Below 1280 the
 * page is the 740 column with the bottom bar, which board 9 draws for a
 * portrait iPad and which fits a landscape one just as well; at 1280 the
 * column beside the rail is 780, the width board 1 was drawn at.
 */
export const RAIL_FROM = 1280

export interface ProposalProps {
  view: ProposalView
  selection: ProposalSelection
  onSelection: (selection: ProposalSelection) => void
  /**
   * Absent on a quote that cannot be approved from here — a draft being
   * previewed, or one already approved. The card is then not drawn at all
   * rather than drawn disabled: a signature box nobody can use is a question
   * the page should not have asked.
   */
  onApprove?: (signature: SignaturePayload) => Promise<void>
  busy?: boolean
  error?: string | null
  /** A short line above the signature, e.g. what happens after approving. */
  footnote?: string
  /**
   * A banner across the top — "Preview — nothing you do here is saved."
   *
   * The page's own, rather than the caller's, so the preview route and the
   * builder's frame cannot each invent a different one above a document that
   * is meant to look like one thing.
   */
  banner?: React.ReactNode
  /**
   * Forces a layout regardless of the window.
   *
   * The builder's preview frame is 390 or 820px wide inside a 1400px window,
   * so media queries are measuring the wrong box: its Phone, iPad and
   * Desktop frames pass `phone`, `tablet` and `desktop`, and follow exactly
   * the rule a real screen of that width would (§10.1). Everything else
   * leaves this alone and the window decides.
   */
  layout?: ProposalLayout
  /** The phone layout — the same as `layout="phone"`, kept for its callers. */
  compact?: boolean
  /**
   * Re-price the page for a selection (`POST /portal/quotes/:id/price`).
   *
   * Only the portal has it: the payment schedule comes out of the billing
   * plans, which this page never receives, so a live schedule needs a round
   * trip. Absent — the office preview, the builder's tab — the opening rows
   * stand while the selection is the one they were priced for and the page
   * says it will confirm the schedule once it moves, which is the honest
   * answer rather than a guess (§4.4).
   */
  onPrice?: PriceCall
  /**
   * The other three answers (§9.4), and the closed states' one action.
   *
   * Only the portal has them: the office preview and the builder's tab are
   * looking at a document, not holding it, and a **Decline** link on the
   * estimator's own screen is a control that cannot mean what it says. Where
   * it is absent the links are not drawn at all.
   */
  responses?: ResponseApi
}

export function Proposal(props: ProposalProps) {
  const {
    view, selection, onSelection, onApprove, busy, error, footnote, banner, onPrice,
    responses,
  } = props
  /** Which of the three dialogs is up, `null` for none (§9.4). */
  const [asking, setAsking] = useState<ResponseKind | null>(null)
  const windowPhone = usePhone()
  /* `max-width` is inclusive, so 1279 is "below RAIL_FROM". */
  const windowNarrow = useNarrow(RAIL_FROM - 1)
  const layout: ProposalLayout = props.layout
    ?? (props.compact === undefined
      ? windowPhone ? 'phone' : windowNarrow ? 'tablet' : 'desktop'
      : props.compact ? 'phone' : 'desktop')
  const phone = layout === 'phone'
  /* Below the rail: the bottom bar, and the sign card as a sheet. */
  const narrow = layout !== 'desktop'
  /** How tall the bottom bar measures, so the footer can clear it. */
  const [barHeight, setBarHeight] = useState(PROPOSAL_BAR)

  const skin = useMemo(() => brandSkin(view.identity), [view.identity])

  /*
   * What the customer would pay for what is ticked right now, from
   * `selectionTotals()` — the one piece of arithmetic this page does, and a
   * sum of amounts the server wrote rather than a second opinion about any
   * price. The server runs the same function before it writes anything, so a
   * disagreement is a bug in `shared/src/proposal.ts` and not a difference of
   * opinion between two implementations.
   */
  const totals = useMemo(() => selectionTotals(view, selection), [view, selection])
  const ready = view.groups.every((group) => selection.choose[group.id] !== undefined)

  /*
   * The same total said by the period, so the **Total · Per month** toggle
   * answers on the tap rather than after a round trip (§4.4). `null` where
   * the quote has no honest "per month" at all, which is the server's call —
   * the toggle is then not drawn.
   */
  const month = useMemo(() => monthSplit(view, selection), [view, selection])

  /*
   * **Total · Per month**, held here because four things follow it: the
   * total card, the rail, the bottom bar and the Included heading. Where
   * nobody has chosen, a quote whose "Price shown as" is the billing plan
   * opens on Per month (Aaron's review, item 2.2) — but only where the toggle
   * exists at all, which is `monthOffered`.
   */
  const [monthMode, setMonthMode] = useMonthMode(view.settings.priceView === 'plan')
  const byMonth = monthOffered(view, month, ready) && monthMode === 'period'

  /*
   * The payment schedule, live where the page can ask for one.
   *
   * `view.payments` were priced on the server for `view.initial` — the one
   * selection it knew about — so the moment somebody picks a different option
   * they describe a choice nobody made. `usePrice` re-asks (debounced, last
   * answer wins) and, where there is nothing to ask, says so rather than
   * printing the wrong schedule.
   */
  const priced = usePrice(selection, view.payments, view.initial, onPrice, view.invoiceTerms)

  const body: BodyProps = {
    // The terms that go with the payments on screen, not the opening ones (§9.4).
    view: { ...view, invoiceTerms: priced.invoiceTerms },
    selection, onSelection, totals, ready, phone, narrow, month,
    monthMode, onMonthMode: setMonthMode,
    priced: priced.priced,
    payments: priced.payments,
  }

  const sections = useMemo((): ProposalSection[] => {
    const included = view.visits.filter((visit) => visit.kind === 'included').length
    const optional = view.visits.filter((visit) => visit.kind === 'optional').length
    const out: ProposalSection[] = []
    /* A chip whose section does not exist is not drawn (§6 item 4). */
    if (included > 0) out.push({ id: SECTION_IDS.included, label: 'Included', count: included })
    if (view.groups.length > 0) {
      /* Board 1's chip says what the section asks for, whatever its heading. */
      out.push({ id: SECTION_IDS.choice, label: 'Choose one', count: view.groups.length })
    }
    if (optional > 0) out.push({ id: SECTION_IDS.optional, label: 'Optional', count: optional })
    out.push({ id: SECTION_IDS.total, label: 'Total' })
    if (view.terms) out.push({ id: SECTION_IDS.terms, label: 'Terms' })
    if (view.signed) out.push({ id: SECTION_IDS.sign, label: 'Signed' })
    else if (onApprove) out.push({ id: SECTION_IDS.sign, label: 'Sign' })
    return out
  }, [view, onApprove])

  const toSign = () => document
    .getElementById(onApprove ? SECTION_IDS.sign : SECTION_IDS.total)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  /*
   * **A closed link is not a document** (§9.2, board 11). Expired, replaced
   * and declined each get a short card saying what happened and the one thing
   * to do about it — and the server sent no prices for any of them, so there
   * is nothing here to hide. The chrome above it is the same, because it is
   * still this company's page.
   */
  const closed = closedPage({
    view,
    ...(responses?.reopen ? { onReopen: responses.reopen } : {}),
  })
  /*
   * **A signed link is the signed copy** (board 11 cell 2): what was accepted,
   * when, the signature, and the terms exactly as they stood — not the whole
   * proposal again with a card at the bottom, because nothing on it can be
   * changed any more and a page full of switches that do nothing is a page
   * that looks broken.
   */
  if (view.signed) {
    return (
      <div style={skin.vars} className="flex min-h-full flex-col bg-bg">
        {banner}
        <ProposalTopBar identity={view.identity} phone={phone} narrow={narrow} />
        <main className={cn('mx-auto flex w-full max-w-[640px] flex-col gap-14 pb-8 pt-[22px]',
          phone ? 'px-4' : 'px-6')}>
          <SignedSection view={view} phone={phone}
            {...(responses ? { onMessage: () => setAsking('question') } : {})} />
          {view.terms && (
            <section id={SECTION_IDS.terms} className="scroll-mt-20">
              <ProposalTermsCard
                terms={view.signed.terms ? { ...view.terms, sections: view.signed.terms } : view.terms}
                allOpen
                phone={phone}
                stamp={<SignedTermsStamp signed={view.signed} />}
              />
            </section>
          )}
        </main>
        <ProposalFooter view={view} phone={phone} narrow={narrow} />
        {responses && (
          <ResponseDialog kind={asking} view={view} api={responses} phone={phone}
            onClose={() => setAsking(null)} />
        )}
      </div>
    )
  }

  if (closed) {
    return (
      <div style={skin.vars} className="flex min-h-full flex-col bg-bg">
        {banner}
        <ProposalTopBar identity={view.identity} phone={phone} narrow={narrow} />
        <main className={cn('mx-auto w-full max-w-[540px] pb-8', phone ? 'px-4' : 'px-6')}>
          {closed}
        </main>
        <ProposalFooter view={view} phone={phone} narrow={narrow} />
      </div>
    )
  }

  return (
    <div style={skin.vars} className="flex min-h-full flex-col bg-bg">
      {banner}
      <ProposalTopBar identity={view.identity} phone={phone} narrow={narrow} />

      <ProposalCover
        reference={view.reference}
        quoteDate={view.quoteDate}
        title={view.title}
        preparedFor={view.preparedFor}
        address={view.propertyAddress}
        band={skin.band}
        phone={phone}
        narrow={narrow}
      />

      {/*
        * `relative z-[1]`, and that is not decoration.
        *
        * The cover band is `relative` with an absolutely-positioned contour
        * SVG inside it, which makes it a positioned element; this row is a
        * later sibling but `static`, so in the painting order it goes
        * *underneath* — and the intro card, pulled up 76px to overlap, spent
        * those 76px behind the green with the top of its text cut off. A
        * later sibling only paints over a positioned one when it is
        * positioned too.
        */}
      <div
        className={cn('relative z-[1] mx-auto flex w-full items-start gap-9',
          /* Board 9: between a phone and a desktop, one centred 740 column. */
          phone ? 'px-4' : narrow ? 'max-w-[820px] px-10' : 'max-w-[1280px] px-[60px]')}
        style={{
          /*
           * The overlap is the whole point of the intro card (§6 item 3): the
           * band is the company and the card is the person who wrote this,
           * and a gap between them makes two unrelated blocks. It is the
           * **row's** margin, so the rail rises with it — board 1 pulls both
           * columns up 76px and their tops line up inside the band.
           */
          marginTop: phone ? -56 : -76,
        }}
      >
        <div
          className="flex min-w-0 flex-1 flex-col"
          style={{ paddingBottom: 0 }}
        >
          <ProposalIntro view={view} phone={phone} />
          {/* Board 1's rhythm: 40 to the chips, 36 to the first section, 56 between. */}
          <div className={phone ? 'h-6' : 'h-10'} />
          <ProposalSectionNav sections={sections} phone={phone} />
          <div className={phone ? 'h-6' : 'h-9'} />

          <div className={cn('flex flex-col', phone ? 'gap-10' : 'gap-14')}>
          <IncludedSection {...body} />
          <ChoiceSection {...body} />
          <OptionalSection {...body} />
          <TotalSection {...body} />
          {view.terms && (
            <section id={SECTION_IDS.terms} className="scroll-mt-20">
              <TermsHead terms={view.terms} phone={phone} />
              <ProposalTermsCard terms={view.terms} phone={phone} />
            </section>
          )}
          {/* Signable, or a preview with nothing to sign. The signed copy is its own page above. */}
          {onApprove && (
            <SignSection
              {...body}
              onApprove={onApprove}
              busy={busy}
              error={error}
              footnote={footnote}
              {...(responses ? { onRespond: setAsking } : {})}
            />
          )}

          </div>
        </div>

        {!narrow && (
          <div className="sticky top-6 shrink-0">
            <ProposalRail
              view={view}
              totals={totals}
              ready={ready}
              priced={priced.priced}
              payments={priced.payments}
              month={view.month && month ? { cadence: view.month, split: month } : null}
              byMonth={byMonth}
              onSign={toSign}
              {...(responses ? { onAsk: () => setAsking('question') } : {})}
              signLabel={view.signed
                ? 'See what you signed'
                : onApprove ? 'Review and sign' : 'See the total'}
            />
          </div>
        )}
      </div>

      <ProposalFooter view={view} phone={phone} narrow={narrow}
        clearance={narrow ? barHeight + 24 : 0} />

      {narrow && (
        <ProposalStickyBar
          total={totals.total}
          ready={ready}
          monthLine={view.month ? monthLine(view.month, month) : null}
          lead={byMonth && view.month && month ? { cadence: view.month, split: month } : null}
          onHeight={setBarHeight}
          phone={phone}
          payments={priced.priced
            ? { count: priced.payments.length, deposit: depositOf(priced.payments) }
            : null}
          onSign={toSign}
          signLabel={view.signed
            ? 'See what you signed'
            : onApprove ? 'Review and sign' : 'See the total'}
        />
      )}

      {/* A dialog on a desktop, a bottom sheet on a phone, one body (§9.4). */}
      {responses && (
        <ResponseDialog
          kind={asking}
          view={view}
          api={responses}
          phone={phone}
          onClose={() => setAsking(null)}
        />
      )}
    </div>
  )
}

/** Board 2: "Terms of service — The agreement you accept when you sign. Version 3, updated Mar 2026." */
function TermsHead({ terms, phone }: { terms: NonNullable<ProposalView['terms']>; phone: boolean }) {
  const updated = new Date(`${terms.updatedOn.slice(0, 10)}T12:00:00`)
    .toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  return (
    <div className={cn('flex flex-col gap-1', phone ? 'mb-3.5' : 'mb-[18px]')}>
      <h2 className={cn('m-0 font-bold tracking-[-0.02em] text-text', phone ? 'text-[22px]' : 'text-[26px]')}>
        Terms of service
      </h2>
      <span className={cn('leading-[1.45] text-text-muted', phone ? 'text-[14px]' : 'text-[15px]')}>
        {`The agreement you accept when you sign. Version ${terms.revision}, updated ${updated}.`}
      </span>
    </div>
  )
}

/**
 * The company's footer, and one grey line that says who built the software.
 *
 * Plan §3.7: "Powered by Rowted" in the footer. It is the only place our name
 * appears on this page — the header carries the company's lockup, because the
 * document is theirs.
 */
function ProposalFooter({ view, phone, narrow = false, clearance = 0 }: {
  view: ProposalView
  phone: boolean
  /** Below the rail: the footer lines up with the page's 740 column. */
  narrow?: boolean
  /**
   * The bottom bar's measured height and a breath above it, where a bar is
   * over the page's foot — so the last line of the footer is read above the
   * bar rather than behind it (Aaron's review, item 2.6).
   */
  clearance?: number
}) {
  const identity = view.identity
  const lines = [
    identity.addressLines.join(', '),
    identity.phone,
    identity.email,
    identity.licence,
  ].filter(Boolean)

  /*
   * Board 2: a white band across the whole page under a hairline, the
   * company's lockup and its own sentence at the left, and our one grey line
   * at the right — never beside their name.
   */
  return (
    <footer className="mt-16 border-t border-line-soft bg-surface">
      <div
        className={cn('mx-auto flex w-full justify-between gap-6',
          phone ? 'flex-col px-4 pb-10 pt-8'
            : narrow ? 'max-w-[820px] items-end px-10 pb-[46px] pt-10'
              : 'max-w-[1280px] items-end px-[60px] pb-[46px] pt-10')}
        style={clearance > 0 ? { paddingBottom: clearance } : undefined}
      >
        <div className="flex max-w-[560px] flex-col gap-2.5">
          <span className="inline-flex items-center gap-2">
            <Lockup identity={identity} size={30} />
            {!identity.logoUrl && (
              <b className="text-[15px] font-bold leading-[1.05] tracking-[-0.015em] text-text">
                {identity.name}
              </b>
            )}
          </span>
          {identity.footer && (
            <span className="text-[13.5px] leading-[1.55] text-text-muted">{identity.footer}</span>
          )}
          {lines.length > 0 && (
            <span className="text-[13px] text-text-faint">{lines.join(' · ')}</span>
          )}
        </div>
        <PoweredBy />
      </div>
    </footer>
  )
}

/**
 * "Powered by Rowted", with our mark just left of our name (Aaron's review,
 * item 2.6) — the plan's own §3.7 words, where this page used to say
 * "Proposals by Rowted".
 *
 * **The app's own `RowtedMark`, not a copy of it**, at 14px and in two grey
 * tokens rather than our green: this is the company's document, and the
 * line is the one place our name appears on it, so it is drawn as quietly as
 * the words beside it. The mark paints with `--color-accent` and
 * `--color-accent-tint`; re-pointing those two at the faint-text and
 * strong-line tokens for this one element is what greys it, and they follow
 * dark mode with the rest of the tokens.
 */
function PoweredBy() {
  return (
    <span data-powered-by className="inline-flex shrink-0 items-center gap-1 text-[12.5px] text-text-faint">
      Powered by{' '}
      <span className="inline-flex items-center gap-[3px]" style={QUIET_MARK}>
        <RowtedMark size={14} />
        Rowted
      </span>
    </span>
  )
}

const QUIET_MARK = {
  '--color-accent': 'var(--color-text-faint)',
  '--color-accent-tint': 'var(--color-line-strong)',
} as React.CSSProperties

export { PROPOSAL_BAR }
